import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Send, Bot } from "lucide-react";
import { API } from "@/lib/api";

export default function Coach() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Oi! Sou o Cria Coach. Me manda tua dúvida sobre grana — reserva, investimento, dívida, cartão, o que for. Tô aqui pra ajudar." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("claude");
  const sessionRef = useRef(`${Date.now()}`);
  const bottomRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    // Scroll only the messages container, not the whole page
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("cria_token");
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: sessionRef.current, model }),
      });
      if (!res.ok) throw new Error("Falhou");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const chunk = line.slice(6);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m]; c[c.length-1] = { role:"assistant", content: "Deu ruim aqui, cria. Tenta de novo." }; return c;
      });
    } finally { setStreaming(false); }
  };

  if (!user) return null;

  if (!user.subscription_active) {
    return (
      <div className="grid-bg min-h-[85vh] flex items-center justify-center px-4">
        <div className="brutal-card brutal-shadow-pink max-w-md" style={{borderColor:'#FF0055'}}>
          <span className="sticker">COACH TRANCADO</span>
          <h2 className="font-bebas text-4xl mt-6 tracking-tighter">Assina pra Falar Comigo</h2>
          <p className="mt-4 text-white/70 font-manrope">O Coach AI é só pra assinantes. Libera por R$ 15,99/mês.</p>
          <button data-testid="coach-paywall-cta" onClick={()=>nav("/pagar/pix")} className="brutal-btn mt-8 w-full">Pagar com Pix</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="coach-page" className="min-h-[85vh] grid-bg">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-4">
          <Bot className="text-[#CCFF00]" size={36}/>
          <div>
            <h1 className="font-bebas text-4xl tracking-tighter">CRIA COACH</h1>
            <p className="text-white/50 font-manrope text-sm">
              {model === "openai" ? "Powered by GPT-5.6 Luna" : "Powered by Claude Sonnet 4.6"}
            </p>
          </div>
          <div className="ml-auto flex gap-2" data-testid="model-toggle">
            <button
              data-testid="model-claude"
              onClick={()=>setModel("claude")}
              disabled={streaming}
              className={`px-3 py-2 font-bebas text-sm border-2 ${model==="claude" ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "text-white/60 border-white/30 hover:border-white"}`}
            >Claude</button>
            <button
              data-testid="model-openai"
              onClick={()=>setModel("openai")}
              disabled={streaming}
              className={`px-3 py-2 font-bebas text-sm border-2 ${model==="openai" ? "bg-[#00F0FF] text-black border-[#00F0FF]" : "text-white/60 border-white/30 hover:border-white"}`}
            >GPT-5.6</button>
          </div>
        </div>

        <div className="mt-8 brutal-card min-h-[60vh] flex flex-col" style={{padding:0}}>
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-6 space-y-4" style={{maxHeight:'60vh'}}>
            {messages.map((m, i) => (
              <div key={i} data-testid={`msg-${i}`} className={`flex ${m.role==='user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-4 border-2 font-manrope whitespace-pre-wrap ${m.role==='user' ? 'bg-[#CCFF00] text-black border-black' : 'bg-[#0A0A0A] text-white border-white/40'}`}>
                  {m.content || <span className="text-white/40">Digitando...</span>}
                </div>
              </div>
            ))}
            <div ref={bottomRef}/>
          </div>
          <div className="border-t-2 border-white/20 p-4 flex gap-3">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              onKeyDown={(e)=> e.key==='Enter' && send()}
              placeholder="Manda tua dúvida..."
              disabled={streaming}
              className="brutal-input flex-1"
            />
            <button data-testid="chat-send" onClick={send} disabled={streaming || !input.trim()} className="brutal-btn px-6">
              <Send size={20}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
